// The boards list's order (Round 6, 21 Oct): folders and the boards in no folder share one order, so a board can sit
// above the folders, between them or below them (David: Overview at the very top, above Docs, Blogs and Plans, in no
// folder). A folder's place is a number in the boards' own order. A folder from before this (no number) shows first,
// in the folders' list order, as every folder did, until the list is next arranged. The drop-down, the phone's list,
// read_board and organize_boards all read the list from here.

import type { Board, BoardFolder, ID } from "./types";

/** One line of the list's top level: a folder with its boards in their order, or a board in no folder. */
export type ListEntry = { folder: BoardFolder; boards: Board[] } | { board: Board };

const placed = (f: BoardFolder) => typeof f.order === "number" && Number.isFinite(f.order);

/** The list's top level, top to bottom, as the drop-down shows it. */
export function topLevel(folders: BoardFolder[], boards: Board[]): ListEntry[] {
  const sorted = [...boards].sort((a, b) => a.order - b.order);
  const known = new Set(folders.map((f) => f.id));
  const lines = [
    ...folders.map((folder, i) => ({ at: placed(folder) ? folder.order! : -1e7 + i, rank: 0, line: { folder, boards: sorted.filter((b) => b.folder === folder.id) } as ListEntry })),
    ...sorted.filter((b) => !b.folder || !known.has(b.folder)).map((board) => ({ at: board.order, rank: 1, line: { board } as ListEntry })),
  ];
  // a folder and a board on one number: the folder first, as folders always came first
  return lines.sort((a, b) => a.at - b.at || a.rank - b.rank).map((x) => x.line);
}

/** Every board in the list's order, top to bottom: a folder's boards where the folder stands. */
export function inListOrder(folders: BoardFolder[], boards: Board[]): Board[] {
  return topLevel(folders, boards).flatMap((e) => ("folder" in e ? e.boards : [e.board]));
}

/**
 * The list numbered top to bottom as it now stands, a folder and then its boards: each board's number and folder,
 * and the folders in their order with theirs. A board numbered here never swaps back with one that shared its number.
 */
export function numbered(entries: ListEntry[]): { boards: Map<ID, { order: number; folder: ID | null }>; folders: BoardFolder[] } {
  const boards = new Map<ID, { order: number; folder: ID | null }>();
  const folders: BoardFolder[] = [];
  let n = 0;
  for (const e of entries) {
    if ("board" in e) boards.set(e.board.id, { order: n++, folder: null });
    else {
      folders.push({ ...e.folder, order: n++ });
      for (const b of e.boards) boards.set(b.id, { order: n++, folder: e.folder.id });
    }
  }
  return { boards, folders };
}

/** A copy of the list to change, its folders' board lists copied too. */
export function copyList(entries: ListEntry[]): ListEntry[] {
  return entries.map((e) => ("folder" in e ? { folder: e.folder, boards: [...e.boards] } : e));
}

/** Where a board or folder stands at the top level, or -1; a board in a folder stands where its folder does. */
export function lineOf(entries: ListEntry[], ref: { board?: ID; folder?: ID }): number {
  return entries.findIndex((e) => ("folder" in e ? e.folder.id === ref.folder || (!!ref.board && e.boards.some((b) => b.id === ref.board)) : e.board.id === ref.board));
}

/** Takes a board out of the list, wherever it is: the board, or null when it is not there. */
export function takeBoard(entries: ListEntry[], id: ID): Board | null {
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if ("board" in e) {
      if (e.board.id === id) return (entries.splice(i, 1), e.board);
    } else {
      const k = e.boards.findIndex((b) => b.id === id);
      if (k >= 0) return e.boards.splice(k, 1)[0];
    }
  }
  return null;
}

/** The list with its top-level line from moved to stand at to (both top-level places), the rest shifting along one. */
export function moveLine(entries: ListEntry[], from: number, to: number): ListEntry[] {
  const next = copyList(entries);
  if (from < 0 || to < 0 || from >= next.length || to >= next.length || from === to) return next;
  const [line] = next.splice(from, 1);
  next.splice(to, 0, line);
  return next;
}

/**
 * A board put where another stands, taking its place and its folder: before it when it comes from below, after it
 * when it comes from above, as a dragged row lands. Dropped on a board in no folder, it stands in no folder too.
 */
export function placeAt(entries: ListEntry[], id: ID, target: ID): ListEntry[] {
  const flat = entries.flatMap((e) => ("folder" in e ? e.boards : [e.board])).map((b) => b.id);
  const from = flat.indexOf(id);
  const to = flat.indexOf(target);
  if (from < 0 || to < 0 || from === to) return copyList(entries);
  const after = from < to;
  const next = copyList(entries);
  const board = takeBoard(next, id)!;
  for (let i = 0; i < next.length; i++) {
    const e = next[i];
    if ("board" in e && e.board.id === target) {
      next.splice(after ? i + 1 : i, 0, { board });
      return next;
    }
    if ("folder" in e) {
      const k = e.boards.findIndex((b) => b.id === target);
      if (k >= 0) {
        e.boards.splice(after ? k + 1 : k, 0, board);
        return next;
      }
    }
  }
  return entries;
}

/** A board out of its folder (or from its place) to stand in no folder at a top-level place. */
export function placeLoose(entries: ListEntry[], id: ID, at: number): ListEntry[] {
  const next = copyList(entries);
  const was = lineOf(next, { board: id });
  const board = takeBoard(next, id);
  if (!board) return copyList(entries);
  // a line taken from above the place moves the place up one
  const gone = was >= 0 && was < at && next.length < entries.length ? 1 : 0;
  next.splice(Math.max(0, Math.min(next.length, at - gone)), 0, { board });
  return next;
}

/** The number after every board's and folder's: a new board or folder there stands at the foot of the list. */
export function nextOrder(folders: BoardFolder[], boards: Board[]): number {
  return Math.max(-1, ...boards.map((b) => b.order), ...folders.map((f) => (placed(f) ? f.order! : -1))) + 1;
}

/** The most boards the number keys open: 1 to 9. */
export const KEYED_BOARDS = 9;

/**
 * The boards the number keys open (Features 2, 21 Oct), 1 the first: the list's order as the drop-down shows it,
 * top to bottom, a folder's boards where the folder stands, and a folded folder's boards left out, so each number
 * is a board the list shows. The first nine.
 */
export function keyedBoards(folders: BoardFolder[], boards: Board[], isOpen: (folder: ID) => boolean): Board[] {
  return topLevel(folders, boards)
    .flatMap((e) => ("folder" in e ? (isOpen(e.folder.id) ? e.boards : []) : [e.board]))
    .slice(0, KEYED_BOARDS);
}
